import { useEffect, useMemo, useRef } from "react";
import { recipeById } from "../../data/recipes";
import { activeSelectionCount } from "../../domain/candidates";
import { instantiateRecipe } from "../../domain/instantiateRecipe";
import { pluralize } from "../../domain/pluralize";
import {
  COOKING_LOCATIONS,
  FOOD_TYPES,
  type Candidate,
  type Locale,
  type SelectionState,
} from "../../domain/types";

const foodTypeLabels = new Map(
  FOOD_TYPES.map((option) => [option.id, option.label]),
);
const cookingLocationLabels = new Map(
  COOKING_LOCATIONS.map((option) => [option.id, option.label]),
);

interface Props {
  candidates: Candidate[];
  selection: SelectionState;
  locale?: Locale;
  onOpen: (recipeId: string) => void;
  limit?: number;
  returnFocusRecipeId?: string | null;
}

export function RecipeResults({
  candidates,
  selection,
  locale = "en",
  onOpen,
  limit = 3,
  returnFocusRecipeId = null,
}: Props) {
  const returnFocusRef = useRef<HTMLButtonElement>(null);
  const selectionCount = activeSelectionCount(selection);
  const visible = useMemo(
    () =>
      candidates.slice(0, limit).flatMap((candidate) => {
        const family = recipeById.get(candidate.recipeId);
        if (!family) return [];
        const recipe = instantiateRecipe(
          family,
          candidate,
          selection.ingredientIds,
          {},
          selection.veganOnly,
          locale,
        );
        if (!recipe) return [];
        const missingEssentialCount = recipe.ingredients.filter(
          (ingredient) => ingredient.group === "add",
        ).length;
        const matchedSelections =
          candidate.matchedIngredientCount +
          (selectionCount - selection.ingredientIds.length);
        return [
          {
            family,
            candidate,
            recipe,
            missingEssentialCount,
            matchedSelections,
          },
        ];
      }),
    [candidates, limit, selection, locale, selectionCount],
  );

  useEffect(() => {
    if (!returnFocusRecipeId) return;
    const fallback = document.getElementById("results-title");
    (returnFocusRef.current ?? fallback)?.focus();
  }, [returnFocusRecipeId]);

  return (
    <div className="recipe-grid">
      {visible.map(
        (
          {
            family,
            candidate,
            recipe,
            missingEssentialCount,
            matchedSelections,
          },
          index,
        ) => (
          <article className="recipe-card" key={family.id}>
            <div className="recipe-card__index" aria-hidden="true">
              {String(index + 1).padStart(2, "0")}
            </div>
            <div className="recipe-card__body">
              <div className="recipe-card__meta">
                <span>{foodTypeLabels.get(family.foodType)}</span>
                <span>
                  {cookingLocationLabels.get(family.primaryCookingLocation)}
                </span>
                <span>{family.totalMinutes} min</span>
                <span>{recipe.vegan ? "Vegan" : "Vegetarian"}</span>
              </div>
              <h3>{recipe.title}</h3>
              <p className="recipe-card__fit">
                {selectionCount > 0
                  ? matchedSelections === selectionCount
                    ? `Uses all ${selectionCount} ${pluralize(selectionCount, locale, { en: ["selection", "selections"] })}`
                    : `Uses ${matchedSelections} of ${selectionCount} selections`
                  : "A flexible place to begin"}
              </p>
              <div className="recipe-card__footer">
                <span>
                  Add {missingEssentialCount}{" "}
                  {pluralize(missingEssentialCount, locale, {
                    en: ["essential", "essentials"],
                  })}
                </span>
                {candidate.selectedSubstitutionIds.length > 0 && (
                  <span>Includes a reviewed swap</span>
                )}
              </div>
              <button
                className="recipe-link"
                aria-label={`View recipe: ${recipe.title}`}
                ref={
                  family.id === returnFocusRecipeId ? returnFocusRef : undefined
                }
                onClick={() => onOpen(family.id)}
              >
                View recipe <span aria-hidden="true">→</span>
              </button>
            </div>
          </article>
        ),
      )}
    </div>
  );
}
